import { useState } from "react";
import { toast } from "sonner";
import { Receipt, Download, Undo2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState, Loading, StatusBadge } from "@/components/common";
import { FormDialog } from "@/components/FormDialog";
import { api, useFetch, errMsg, money, fmtDate } from "@/lib/api";
import { useCurrency } from "@/context/AuthContext";

const STATUSES = ["all", "overdue", "partial", "due", "upcoming", "paid"];

function exportCsv(rows) {
  const head = ["Due date", "Renter", "Property", "Amount", "Paid", "Paid date", "Status"];
  const lines = rows.map((r) => [r.due_date, r.renter_name, r.property_name, r.amount, r.paid_amount, r.paid_date || "", r.status]
    .map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(","));
  const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "rent-ledger.csv";
  a.click();
}

export default function Rent() {
  const cur = useCurrency();
  const [status, setStatus] = useState("all");
  const [month, setMonth] = useState("");
  const qs = new URLSearchParams({ ...(status !== "all" && { status }), ...(month && { month }) }).toString();
  const { data, reload } = useFetch(`/biz/rent${qs ? `?${qs}` : ""}`);
  const [pay, setPay] = useState(null);
  const total = (k) => (data || []).reduce((s, r) => s + (k === "outstanding" ? r.amount - (r.paid_amount || 0) : r[k] || 0), 0);
  const undo = async (r) => {
    if (!window.confirm("Undo this payment?")) return;
    try { await api.post(`/biz/rent/${r.id}/unpay`); toast.success("Payment removed"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="rent-page">
      <PageHeader eyebrow="Ledger" title="Rent" subtitle="Record payments as they arrive. Overdue rent shows up automatically."
        actions={<Button variant="outline" onClick={() => exportCsv(data || [])} data-testid="rent-export-csv"><Download className="w-4 h-4 mr-1" />Export CSV</Button>} />
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="flex-wrap h-auto">{STATUSES.map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`rent-filter-${s}`}>{s}</TabsTrigger>)}</TabsList>
        </Tabs>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-48" data-testid="rent-month-filter" />
        {month && <Button variant="ghost" size="sm" onClick={() => setMonth("")}>Clear month</Button>}
      </div>
      {!data ? <Loading /> : data.length === 0 ? <EmptyState icon={Receipt} title="Nothing here" text="No rent entries match this filter." /> : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
            <div className="panel py-4"><div className="text-slate-500">Expected</div><div className="font-display text-xl font-bold" data-testid="rent-total-expected">{money(total("amount"), cur)}</div></div>
            <div className="panel py-4"><div className="text-slate-500">Received</div><div className="font-display text-xl font-bold text-emerald-700" data-testid="rent-total-received">{money(total("paid_amount"), cur)}</div></div>
            <div className="panel py-4"><div className="text-slate-500">Outstanding</div><div className="font-display text-xl font-bold text-amber-700" data-testid="rent-total-outstanding">{money(total("outstanding"), cur)}</div></div>
          </div>
          <div className="panel p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Due</TableHead><TableHead>Renter</TableHead><TableHead>Property</TableHead><TableHead>Amount</TableHead><TableHead>Paid</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {data.map((r) => (
                  <TableRow key={r.id} data-testid={`rent-row-${r.id}`}>
                    <TableCell className="whitespace-nowrap">{fmtDate(r.due_date)}</TableCell>
                    <TableCell className="font-semibold">{r.renter_name}</TableCell>
                    <TableCell className="text-sm">{r.property_name}</TableCell>
                    <TableCell className="font-mono text-sm">{money(r.amount, cur)}</TableCell>
                    <TableCell className="font-mono text-sm">{money(r.paid_amount, cur)}{r.paid_date && <div className="text-xs text-slate-500 font-sans">{fmtDate(r.paid_date)}</div>}</TableCell>
                    <TableCell><StatusBadge status={r.status} testid={`rent-status-${r.id}`} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {r.status !== "paid" && <Button size="sm" onClick={() => setPay(r)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`rent-record-${r.id}`}>Record payment</Button>}
                      {r.paid_amount > 0 && <Button size="icon" variant="ghost" onClick={() => undo(r)} data-testid={`rent-undo-${r.id}`}><Undo2 className="w-4 h-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
      <FormDialog open={!!pay} onOpenChange={(o) => !o && setPay(null)} title="Record rent payment" testid="pay-form"
        description={pay ? `${pay.renter_name} · due ${fmtDate(pay.due_date)}` : ""}
        initial={pay ? { amount: Math.round((pay.amount - (pay.paid_amount || 0)) * 100) / 100, paid_date: new Date().toISOString().slice(0, 10) } : {}}
        fields={[
          { name: "amount", label: "Amount received", type: "number", required: true },
          { name: "paid_date", label: "Date received", type: "date", required: true },
          { name: "method", label: "Method", type: "select", default: "bank_transfer", full: true,
            options: [{ value: "bank_transfer", label: "Bank transfer" }, { value: "standing_order", label: "Standing order" }, { value: "cash", label: "Cash" }, { value: "card", label: "Card" }, { value: "other", label: "Other" }] },
        ]}
        onSubmit={async (v) => { await api.post(`/biz/rent/${pay.id}/pay`, v); toast.success("Payment recorded"); reload(); }} />
    </div>
  );
}
